import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { PageHeader, StatusBadge } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Star, ExternalLink, Briefcase, Pencil, Eye, EyeOff, Wand2, FileText as FilesIcon, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate, fmtDateTime, toInputDate, fromInputDate } from "@/lib/format";
import { toast } from "sonner";
import ChaserComposer from "@/components/ChaserComposer";
import ClientDocuments from "@/components/ClientDocuments";

export default function ClientDetail() {
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [services, setServices] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [notes, setNotes] = useState([]);

  const [contactOpen, setContactOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", role: "", is_primary: false });

  const [serviceOpen, setServiceOpen] = useState(false);
  const [serviceForm, setServiceForm] = useState({ template_id: "", name: "", frequency: "annual", next_deadline: "", reminders_paused: false });

  const [jobOpen, setJobOpen] = useState(false);
  const [jobForm, setJobForm] = useState({ title: "", cs_id: "", deadline: "" });

  const [noteBody, setNoteBody] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [authCodeRevealed, setAuthCodeRevealed] = useState(null);
  const [ggPasswordRevealed, setGgPasswordRevealed] = useState(null);
  const [deadlinePreview, setDeadlinePreview] = useState(null); // {deadlines, requires_year_end, ...}
  const [generating, setGenerating] = useState(false);

  const loadAll = useCallback(async () => {
    const [c, ct, cs, tmpl, jb, nt] = await Promise.all([
      api.get(`/clients/${clientId}`),
      api.get(`/clients/${clientId}/contacts`),
      api.get(`/client-services`, { params: { client_id: clientId } }),
      api.get(`/service-templates`),
      api.get(`/jobs`, { params: { client_id: clientId } }),
      api.get(`/notes`, { params: { client_id: clientId } }),
    ]);
    setClient(c.data);
    setContacts(ct.data);
    setServices(cs.data);
    setTemplates(tmpl.data);
    setJobs(jb.data);
    setNotes(nt.data);
  }, [clientId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const addContact = async (e) => {
    e.preventDefault();
    await api.post(`/clients/${clientId}/contacts`, contactForm);
    setContactOpen(false);
    setContactForm({ name: "", email: "", phone: "", role: "", is_primary: false });
    toast.success("Contact added");
    loadAll();
  };

  const removeContact = async (id) => {
    await api.delete(`/contacts/${id}`);
    loadAll();
  };

  const addService = async (e) => {
    e.preventDefault();
    const payload = {
      client_id: clientId,
      template_id: serviceForm.template_id || null,
      name: serviceForm.name,
      frequency: serviceForm.frequency,
      next_deadline: serviceForm.next_deadline ? fromInputDate(serviceForm.next_deadline) : null,
      reminders_paused: serviceForm.reminders_paused,
    };
    await api.post("/client-services", payload);
    setServiceOpen(false);
    setServiceForm({ template_id: "", name: "", frequency: "annual", next_deadline: "", reminders_paused: false });
    toast.success("Service added");
    loadAll();
  };

  const applyTemplate = (id) => {
    const t = templates.find((x) => x.template_id === id);
    if (t) setServiceForm((f) => ({ ...f, template_id: id, name: t.name, frequency: t.frequency }));
  };

  const addJob = async (e) => {
    e.preventDefault();
    try {
      await api.post("/jobs", {
        client_id: clientId,
        cs_id: jobForm.cs_id || null,
        title: jobForm.title,
        deadline: jobForm.deadline ? fromInputDate(jobForm.deadline) : null,
      });
      setJobOpen(false);
      setJobForm({ title: "", cs_id: "", deadline: "" });
      toast.success("Job created — reminders queued");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const removeJob = async (id) => {
    if (!window.confirm("Delete this job?")) return;
    await api.delete(`/jobs/${id}`);
    loadAll();
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!noteBody.trim()) return;
    await api.post("/notes", { client_id: clientId, body: noteBody });
    setNoteBody("");
    loadAll();
  };

  const openEdit = () => {
    setEditForm({
      name: client.name || "",
      business_name: client.business_name || "",
      client_code: client.client_code || "",
      client_type: client.client_type,
      status: client.status,
      uk_utr: "",  // never pre-fill sensitive fields — leave blank means don't change
      companies_house_no: client.companies_house_no || "",
      year_end_date: toInputDate(client.year_end_date) || "",
      confirmation_statement_date: toInputDate(client.confirmation_statement_date) || "",
      ch_auth_code: "",
      payroll_paye_reference: client.payroll_paye_reference || "",
      payroll_accounts_office_reference: client.payroll_accounts_office_reference || "",
      gg_user_id: client.gg_user_id || "",
      gg_password: "",  // blank = don't change
      drive_folder_url: client.drive_folder_url || "",
    });
    setAuthCodeRevealed(null);
    setGgPasswordRevealed(null);
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    // Only send fields with actual values so we don't overwrite masked/sensitive ones with blanks
    const payload = { name: editForm.name, business_name: editForm.business_name, client_code: editForm.client_code || "", client_type: editForm.client_type, status: editForm.status, companies_house_no: editForm.companies_house_no, drive_folder_url: editForm.drive_folder_url };
    if (editForm.uk_utr) payload.uk_utr = editForm.uk_utr;
    if (editForm.year_end_date) payload.year_end_date = fromInputDate(editForm.year_end_date);
    if (editForm.confirmation_statement_date) payload.confirmation_statement_date = fromInputDate(editForm.confirmation_statement_date);
    if (editForm.ch_auth_code) payload.ch_auth_code = editForm.ch_auth_code;
    // Always send PAYE / GG-user-id — they're not sensitive and can be cleared to blank
    payload.payroll_paye_reference = editForm.payroll_paye_reference || "";
    payload.payroll_accounts_office_reference = editForm.payroll_accounts_office_reference || "";
    payload.gg_user_id = editForm.gg_user_id || "";
    if (editForm.gg_password) payload.gg_password = editForm.gg_password;
    try {
      await api.patch(`/clients/${clientId}`, payload);
      toast.success("Client updated");
      setEditOpen(false);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const revealAuthCode = async () => {
    try {
      const r = await api.get(`/clients/${clientId}/reveal-auth-code`);
      setAuthCodeRevealed(r.data.ch_auth_code || "(none stored)");
    } catch { toast.error("Failed to reveal"); }
  };

  const revealGgPassword = async () => {
    try {
      const r = await api.get(`/clients/${clientId}/reveal-gg-password`);
      setGgPasswordRevealed(r.data.gg_password || "(none stored)");
    } catch { toast.error("Failed to reveal"); }
  };

  const toggleAmlCheck = async (next) => {
    try {
      const r = await api.post(`/clients/${clientId}/aml-check`, { completed: !!next });
      setClient(r.data);
      toast.success(next ? "AML checks marked complete" : "AML checks reset");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const [chSyncing, setChSyncing] = useState(false);
  const syncFromCompaniesHouse = async () => {
    if (!client?.companies_house_no) {
      toast.error("Add a Companies House number first");
      return;
    }
    setChSyncing(true);
    try {
      const r = await api.get(`/companies-house/company/${client.companies_house_no}`);
      const p = r.data;
      // Compute the year-end anchor: prefer next accounts made-up-to; else ARD in current year.
      let yearEnd = p.accounts_next_made_up_to || "";
      if (!yearEnd && p.accounting_reference_day && p.accounting_reference_month) {
        const y = new Date().getFullYear();
        const mm = String(p.accounting_reference_month).padStart(2, "0");
        const dd = String(p.accounting_reference_day).padStart(2, "0");
        yearEnd = `${y}-${mm}-${dd}`;
      }
      const csDate = p.confirmation_statement_next_due || p.confirmation_statement_next_made_up_to || "";
      // Diff current vs incoming so we can tell the user what actually changed.
      const current = {
        year_end_date: (client.year_end_date || "").slice(0, 10),
        confirmation_statement_date: (client.confirmation_statement_date || "").slice(0, 10),
        business_name: client.business_name || "",
      };
      const incoming = {
        year_end_date: (yearEnd || "").slice(0, 10),
        confirmation_statement_date: (csDate || "").slice(0, 10),
        business_name: p.company_name || "",
      };
      const changed = Object.keys(incoming).filter((k) => incoming[k] && incoming[k] !== current[k]);
      if (changed.length === 0) {
        toast.success("Already in sync with Companies House");
        return;
      }
      const patch = {
        name: client.name,
        client_type: "ltd",
        status: client.status,
        business_name: incoming.business_name,
        companies_house_no: p.company_number,
      };
      if (incoming.year_end_date) patch.year_end_date = incoming.year_end_date;
      if (incoming.confirmation_statement_date) patch.confirmation_statement_date = incoming.confirmation_statement_date;
      const upd = await api.patch(`/clients/${clientId}`, patch);
      setClient(upd.data);
      const labels = { year_end_date: "Year-end", confirmation_statement_date: "Confirmation statement", business_name: "Business name" };
      toast.success(`Synced: ${changed.map((k) => labels[k]).join(", ")}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Companies House sync failed");
    } finally {
      setChSyncing(false);
    }
  };

  const openDeadlinePreview = async () => {
    try {
      const r = await api.get(`/clients/${clientId}/uk-deadline-preview`);
      setDeadlinePreview(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const confirmGenerate = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/clients/${clientId}/generate-uk-deadlines`);
      toast.success(`Generated ${r.data.total_created} deadline(s)${r.data.skipped_duplicates.length ? ` · skipped ${r.data.skipped_duplicates.length} duplicate(s)` : ""}`);
      setDeadlinePreview(null);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setGenerating(false); }
  };

  if (!client) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  return (
    <div>
      <PageHeader
        testid="page-client-detail"
        title={client.business_name || client.name}
        subtitle={`${client.client_type.replace("_", " ")} · UTR ${client.uk_utr_redacted || "—"}`}
        actions={
          <div className="flex items-center gap-2">
            <StatusBadge status={client.status} />
            <Button data-testid="generate-deadlines-btn" variant="outline" onClick={openDeadlinePreview} className="rounded-md">
              <Wand2 className="h-4 w-4 mr-2" /> Generate UK deadlines
            </Button>
            <Button data-testid="edit-client-btn" variant="outline" onClick={openEdit} className="rounded-md">
              <Pencil className="h-4 w-4 mr-2" /> Edit
            </Button>
            <ChaserComposer clientId={clientId} contacts={contacts} onCreated={loadAll} />
            {client.drive_folder_url && (
              <a
                data-testid="drive-folder-link"
                href={client.drive_folder_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <ExternalLink className="h-3.5 w-3.5" /> Drive folder
              </a>
            )}
          </div>
        }
      />

      <div className="p-6 lg:p-8">
        <Tabs defaultValue="overview">
          <TabsList data-testid="client-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts</TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services">Services</TabsTrigger>
            <TabsTrigger value="jobs" data-testid="tab-jobs">Jobs</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoBlock title="Client details">
                <Row k="Name" v={client.name} />
                <Row k="Business" v={client.business_name || "—"} />
                <Row k="Client code" v={<span className="font-mono" data-testid="detail-client-code">{client.client_code || "—"}</span>} />
                <Row k="Type" v={client.client_type.replace("_", " ")} />
                <Row k="UTR (redacted)" v={<span className="font-mono">{client.uk_utr_redacted || "—"}</span>} />
                <Row k="Companies House no." v={
                  <div className="flex items-center gap-2">
                    <span className="font-mono">{client.companies_house_no || "—"}</span>
                    {client.companies_house_no && (
                      <button
                        type="button"
                        data-testid="ch-sync-btn"
                        onClick={syncFromCompaniesHouse}
                        disabled={chSyncing}
                        className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-slate-900 border border-slate-200 rounded px-1.5 py-0.5 disabled:opacity-50"
                        title="Refresh year-end and confirmation-statement dates from Companies House"
                      >
                        <RefreshCw className={`h-3 w-3 ${chSyncing ? "animate-spin" : ""}`} />
                        {chSyncing ? "Syncing…" : "Sync from CH"}
                      </button>
                    )}
                  </div>
                } />
                <Row k="Year-end" v={<span className="font-mono">{fmtDate(client.year_end_date)}</span>} />
                <Row k="Confirmation statement" v={<span className="font-mono">{fmtDate(client.confirmation_statement_date)}</span>} />
                <Row k="CH auth code" v={
                  <div className="flex items-center gap-2">
                    <span className="font-mono">
                      {authCodeRevealed !== null
                        ? authCodeRevealed
                        : (client.ch_auth_code_present ? (client.ch_auth_code_masked || "••••••") : "—")}
                    </span>
                    {client.ch_auth_code_present && (
                      <button
                        data-testid="reveal-ch-code-btn"
                        onClick={() => authCodeRevealed !== null ? setAuthCodeRevealed(null) : revealAuthCode()}
                        className="text-slate-500 hover:text-slate-900 transition-colors"
                        title={authCodeRevealed !== null ? "Hide" : "Reveal (audit-logged)"}
                      >
                        {authCodeRevealed !== null ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </button>
                    )}
                  </div>
                } />
                <Row k="Employer PAYE ref" v={<span className="font-mono">{client.payroll_paye_reference || "—"}</span>} />
                <Row k="Accounts Office ref" v={<span className="font-mono">{client.payroll_accounts_office_reference || "—"}</span>} />
                <Row k="Gateway user ID" v={<span className="font-mono">{client.gg_user_id || "—"}</span>} />
                <Row k="Gateway password" v={
                  <div className="flex items-center gap-2">
                    <span className="font-mono">
                      {ggPasswordRevealed !== null
                        ? ggPasswordRevealed
                        : (client.gg_password_present ? "••••••••••••" : "—")}
                    </span>
                    {client.gg_password_present && (
                      <button
                        data-testid="reveal-gg-password-btn"
                        onClick={() => ggPasswordRevealed !== null ? setGgPasswordRevealed(null) : revealGgPassword()}
                        className="text-slate-500 hover:text-slate-900 transition-colors"
                        title={ggPasswordRevealed !== null ? "Hide" : "Reveal (audit-logged)"}
                      >
                        {ggPasswordRevealed !== null ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </button>
                    )}
                  </div>
                } />
                <Row k="Status" v={<StatusBadge status={client.status} />} />
                <Row k="AML checks" v={
                  <label className="inline-flex items-center gap-2 cursor-pointer select-none" title={client.aml_check_completed && client.aml_check_completed_at ? `Marked ${fmtDateTime(client.aml_check_completed_at)}${client.aml_check_completed_by ? ` by ${client.aml_check_completed_by}` : ""}` : "Tick when you have completed KYC/AML checks for this client"}>
                    <Checkbox
                      data-testid="aml-check-toggle"
                      checked={!!client.aml_check_completed}
                      onCheckedChange={(v) => toggleAmlCheck(!!v)}
                    />
                    <span className={`text-xs ${client.aml_check_completed ? "text-emerald-700 font-medium" : "text-slate-500"}`} data-testid="aml-check-status">
                      {client.aml_check_completed
                        ? `Completed${client.aml_check_completed_at ? ` · ${fmtDate(client.aml_check_completed_at)}` : ""}`
                        : "Not completed"}
                    </span>
                  </label>
                } />
              </InfoBlock>

              <InfoBlock title="Activity">
                <Row k="Contacts" v={contacts.length} />
                <Row k="Services" v={services.length} />
                <Row k="Jobs" v={jobs.length} />
                <Row k="Notes" v={notes.length} />
                <Row k="Created" v={fmtDate(client.created_at)} />
              </InfoBlock>
            </div>
          </TabsContent>

          <TabsContent value="contacts" className="mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-600">{contacts.length} contact(s)</div>
              <Dialog open={contactOpen} onOpenChange={setContactOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-contact-btn" className="rounded-md bg-slate-900 hover:bg-slate-800">
                    <Plus className="h-4 w-4 mr-2" /> Add contact
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader><DialogTitle>New contact</DialogTitle></DialogHeader>
                  <form onSubmit={addContact} className="space-y-3">
                    <div><Label>Name</Label><Input data-testid="contact-name-input" required value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} /></div>
                    <div><Label>Email</Label><Input data-testid="contact-email-input" type="email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Phone</Label><Input value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} /></div>
                      <div><Label>Role</Label><Input value={contactForm.role} onChange={(e) => setContactForm({ ...contactForm, role: e.target.value })} /></div>
                    </div>
                    <label className="inline-flex items-center gap-2 text-sm">
                      <Checkbox data-testid="contact-primary-checkbox" checked={contactForm.is_primary} onCheckedChange={(v) => setContactForm({ ...contactForm, is_primary: !!v })} />
                      Set as primary contact (receives reminders)
                    </label>
                    <DialogFooter><Button data-testid="save-contact-btn" type="submit" className="bg-slate-900 hover:bg-slate-800">Save</Button></DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.1em] text-slate-500">
                  <tr><th className="text-left px-4 py-2">Name</th><th className="text-left px-4 py-2">Email</th><th className="text-left px-4 py-2">Phone</th><th className="text-left px-4 py-2">Role</th><th></th></tr>
                </thead>
                <tbody>
                  {contacts.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No contacts yet.</td></tr>}
                  {contacts.map((c) => (
                    <tr key={c.contact_id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 flex items-center gap-2">
                        {c.is_primary && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />}
                        <span className="font-medium text-slate-900">{c.name}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{c.email || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{c.phone || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{c.role || "—"}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => removeContact(c.contact_id)} className="text-slate-400 hover:text-rose-600 transition-colors"><Trash2 className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="services" className="mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-600">{services.length} service(s) engaged</div>
              <Dialog open={serviceOpen} onOpenChange={setServiceOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-service-btn" className="rounded-md bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4 mr-2" /> Add service</Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader><DialogTitle>Engage service</DialogTitle></DialogHeader>
                  <form onSubmit={addService} className="space-y-3">
                    <div>
                      <Label>Template</Label>
                      <Select value={serviceForm.template_id} onValueChange={applyTemplate}>
                        <SelectTrigger data-testid="service-template-select"><SelectValue placeholder="Pick a template…" /></SelectTrigger>
                        <SelectContent>
                          {templates.map((t) => <SelectItem key={t.template_id} value={t.template_id}>{t.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Name shown to client</Label><Input required value={serviceForm.name} onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })} /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Frequency</Label>
                        <Select value={serviceForm.frequency} onValueChange={(v) => setServiceForm({ ...serviceForm, frequency: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="once">Once</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="quarterly">Quarterly</SelectItem>
                            <SelectItem value="annual">Annual</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Next deadline</Label>
                        <Input type="date" value={serviceForm.next_deadline} onChange={(e) => setServiceForm({ ...serviceForm, next_deadline: e.target.value })} />
                      </div>
                    </div>
                    <DialogFooter><Button type="submit" data-testid="save-service-btn" className="bg-slate-900 hover:bg-slate-800">Add service</Button></DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.1em] text-slate-500">
                  <tr><th className="text-left px-4 py-2">Service</th><th className="text-left px-4 py-2">Frequency</th><th className="text-left px-4 py-2">Next deadline</th><th className="text-left px-4 py-2">Reminders</th></tr>
                </thead>
                <tbody>
                  {services.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-500">No services engaged.</td></tr>}
                  {services.map((s) => (
                    <tr key={s.cs_id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-slate-900">{s.name}</td>
                      <td className="px-4 py-3 text-slate-600 capitalize">{s.frequency}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-800">{fmtDate(s.next_deadline)}</td>
                      <td className="px-4 py-3"><StatusBadge status={s.reminders_paused ? "paused" : "active"} label={s.reminders_paused ? "Paused" : "Active"} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="jobs" className="mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-600">{jobs.length} job(s)</div>
              <Dialog open={jobOpen} onOpenChange={setJobOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-job-btn" className="rounded-md bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4 mr-2" /> New job</Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader><DialogTitle>New job</DialogTitle></DialogHeader>
                  <form onSubmit={addJob} className="space-y-3">
                    <div><Label>Job title *</Label><Input required value={jobForm.title} onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })} data-testid="job-title-input" /></div>
                    <div>
                      <Label>Link to service</Label>
                      <Select value={jobForm.cs_id} onValueChange={(v) => setJobForm({ ...jobForm, cs_id: v })}>
                        <SelectTrigger><SelectValue placeholder="(optional)" /></SelectTrigger>
                        <SelectContent>
                          {services.map((s) => <SelectItem key={s.cs_id} value={s.cs_id}>{s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Deadline (editable, calculated dates flagged)</Label>
                      <Input type="date" value={jobForm.deadline} onChange={(e) => setJobForm({ ...jobForm, deadline: e.target.value })} data-testid="job-deadline-input" />
                      <div className="text-[11px] text-amber-700 mt-1">UK deadlines can be exceptional — always review before saving.</div>
                    </div>
                    <DialogFooter><Button data-testid="save-job-btn" type="submit" className="bg-slate-900 hover:bg-slate-800">Create job</Button></DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.1em] text-slate-500">
                  <tr><th className="text-left px-4 py-2">Title</th><th className="text-left px-4 py-2">Stage</th><th className="text-left px-4 py-2">Status</th><th className="text-left px-4 py-2">Deadline</th><th></th></tr>
                </thead>
                <tbody>
                  {jobs.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500"><Briefcase className="h-5 w-5 mx-auto text-slate-300 mb-2" />No jobs yet.</td></tr>}
                  {jobs.map((j) => (
                    <tr key={j.job_id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <Link to={`/jobs/${j.job_id}`} className="font-medium text-slate-900 hover:text-blue-600 transition-colors">{j.title}</Link>
                        {j.deadline_calculated && (
                          <span className="ml-2 inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800">
                            calculated
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{j.stage}</td>
                      <td className="px-4 py-3"><StatusBadge status={j.status} /></td>
                      <td className="px-4 py-3 font-mono text-xs">{fmtDate(j.deadline)}</td>
                      <td className="px-4 py-3 text-right"><button onClick={() => removeJob(j.job_id)} className="text-slate-400 hover:text-rose-600 transition-colors"><Trash2 className="h-4 w-4" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="documents" className="mt-4 space-y-4">
            <ClientDocuments clientId={clientId} />
          </TabsContent>


          <TabsContent value="notes" className="mt-4 space-y-4">
            <form onSubmit={addNote} className="border border-slate-200 rounded-md bg-white p-4 space-y-2">              <Textarea data-testid="note-textarea" placeholder="Add a note about this client…" value={noteBody} onChange={(e) => setNoteBody(e.target.value)} />
              <div className="text-right"><Button data-testid="save-note-btn" type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-md">Post note</Button></div>
            </form>
            <ul className="space-y-2">
              {notes.map((n) => (
                <li key={n.note_id} className="border border-slate-200 rounded-md bg-white p-4">
                  <div className="text-xs text-slate-500 mb-2 flex items-center justify-between">
                    <span>{n.author_name}</span>
                    <span className="font-mono">{fmtDateTime(n.created_at)}</span>
                  </div>
                  <div className="text-sm text-slate-800 whitespace-pre-wrap">{n.body}</div>
                </li>
              ))}
              {notes.length === 0 && <div className="text-sm text-slate-500 text-center py-8">No notes yet.</div>}
            </ul>
          </TabsContent>
        </Tabs>
      </div>

      {/* Deadline generation preview dialog */}
      <Dialog open={!!deadlinePreview} onOpenChange={() => setDeadlinePreview(null)}>
        <DialogContent className="max-w-2xl" data-testid="deadline-preview-dialog">
          <DialogHeader>
            <DialogTitle>Generate UK deadlines</DialogTitle>
          </DialogHeader>
          {deadlinePreview && (
            <div className="space-y-3 text-sm">
              <div className="text-slate-600">
                Standard UK filing deadlines will be calculated from this client's dates.
                Each generated job is marked <span className="text-amber-700 font-medium">calculated</span> and remains fully editable.
              </div>
              {(deadlinePreview.requires_year_end || deadlinePreview.requires_confirmation_statement) && (
                <div className="text-xs bg-amber-50 border border-amber-200 rounded-md p-3 text-amber-900">
                  {deadlinePreview.requires_year_end && <div>• No year-end date on file — Corporation Tax / Accounts deadlines will be skipped.</div>}
                  {deadlinePreview.requires_confirmation_statement && <div>• No confirmation statement date — CS01 will be skipped.</div>}
                  <div className="mt-1">Edit the client to add these dates for a complete calendar.</div>
                </div>
              )}
              {deadlinePreview.deadlines.length === 0 ? (
                <div className="text-center py-8 text-slate-500">Nothing to generate — add year-end / confirmation-statement dates first, or change client type.</div>
              ) : (
                <ul className="border border-slate-200 rounded-md divide-y divide-slate-100">
                  {deadlinePreview.deadlines.map((d, i) => (
                    <li key={i} className="px-4 py-3 flex items-center justify-between">
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900">{d.title}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{d.calculation_note}</div>
                      </div>
                      <div className="text-right shrink-0 ml-4">
                        <div className="font-mono text-xs text-slate-800">{fmtDate(d.deadline)}</div>
                        {d.duplicate && <div className="text-[11px] text-slate-500">already exists</div>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeadlinePreview(null)}>Cancel</Button>
            {deadlinePreview?.deadlines?.some((d) => !d.duplicate) && (
              <Button data-testid="confirm-generate-btn" onClick={confirmGenerate} disabled={generating} className="bg-slate-900 hover:bg-slate-800">
                {generating ? "Creating…" : "Create these deadlines"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit client dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-client-dialog">
          <DialogHeader><DialogTitle>Edit client</DialogTitle></DialogHeader>
          {editForm && (
            <form onSubmit={saveEdit} className="space-y-3">
              <div className="grid grid-cols-[1fr,1fr,140px] gap-3">
                <div>
                  <Label>Name *</Label>
                  <Input required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                </div>
                <div>
                  <Label>Business name</Label>
                  <Input value={editForm.business_name} onChange={(e) => setEditForm({ ...editForm, business_name: e.target.value })} />
                </div>
                <div>
                  <Label>Client code</Label>
                  <Input data-testid="edit-client-code-input" placeholder="e.g. MAT001" value={editForm.client_code} onChange={(e) => setEditForm({ ...editForm, client_code: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={editForm.client_type} onValueChange={(v) => setEditForm({ ...editForm, client_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Individual</SelectItem>
                      <SelectItem value="sole_trader">Sole trader</SelectItem>
                      <SelectItem value="ltd">Ltd company</SelectItem>
                      <SelectItem value="partnership">Partnership</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="prospect">Prospect</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>UK UTR <span className="text-slate-400">(leave blank to keep)</span></Label>
                  <Input value={editForm.uk_utr} onChange={(e) => setEditForm({ ...editForm, uk_utr: e.target.value })} placeholder={client.uk_utr_redacted || ""} />
                </div>
                <div>
                  <Label>Companies House no.</Label>
                  <Input value={editForm.companies_house_no} onChange={(e) => setEditForm({ ...editForm, companies_house_no: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Year-end date</Label>
                  <Input type="date" value={editForm.year_end_date} onChange={(e) => setEditForm({ ...editForm, year_end_date: e.target.value })} />
                </div>
                <div>
                  <Label>Confirmation statement date</Label>
                  <Input type="date" value={editForm.confirmation_statement_date} onChange={(e) => setEditForm({ ...editForm, confirmation_statement_date: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>CH auth code <span className="text-slate-400">(leave blank to keep)</span></Label>
                <Input type="password" autoComplete="off" placeholder={client.ch_auth_code_present ? (client.ch_auth_code_masked || "••••••") : "not stored"} value={editForm.ch_auth_code} onChange={(e) => setEditForm({ ...editForm, ch_auth_code: e.target.value })} />
                <div className="text-[11px] text-slate-500 mt-1">Sensitive · never included in emails. Reveal on overview is audit-logged.</div>
              </div>
              <div className="pt-2 border-t border-slate-100">
                <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-semibold mb-2">Payroll (PAYE)</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Employer PAYE reference</Label>
                    <Input data-testid="edit-paye-ref" placeholder="e.g. 123/AB456" value={editForm.payroll_paye_reference} onChange={(e) => setEditForm({ ...editForm, payroll_paye_reference: e.target.value })} />
                  </div>
                  <div>
                    <Label>Accounts Office reference</Label>
                    <Input data-testid="edit-accounts-office-ref" placeholder="e.g. 123PA00012345" value={editForm.payroll_accounts_office_reference} onChange={(e) => setEditForm({ ...editForm, payroll_accounts_office_reference: e.target.value })} />
                  </div>
                </div>
              </div>
              <div className="pt-2 border-t border-slate-100">
                <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-semibold mb-2">Government Gateway</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Gateway user ID</Label>
                    <Input data-testid="edit-gg-user-id" placeholder="12-digit ID" value={editForm.gg_user_id} onChange={(e) => setEditForm({ ...editForm, gg_user_id: e.target.value })} />
                  </div>
                  <div>
                    <Label>Gateway password <span className="text-slate-400">(leave blank to keep)</span></Label>
                    <Input data-testid="edit-gg-password" type="password" autoComplete="new-password" placeholder={client.gg_password_present ? "••••••••••" : "not stored"} value={editForm.gg_password} onChange={(e) => setEditForm({ ...editForm, gg_password: e.target.value })} />
                  </div>
                </div>
                <div className="text-[11px] text-slate-500 mt-1">Encrypted at rest · reveal on overview is audit-logged.</div>
              </div>
              <div>
                <Label>Drive folder URL</Label>
                <Input value={editForm.drive_folder_url} onChange={(e) => setEditForm({ ...editForm, drive_folder_url: e.target.value })} />
              </div>
              <DialogFooter className="sticky bottom-0 -mx-6 -mb-6 px-6 py-4 bg-background border-t border-slate-200">
                <Button data-testid="save-edit-btn" type="submit" className="bg-slate-900 hover:bg-slate-800">Save changes</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoBlock({ title, children }) {
  return (
    <div className="border border-slate-200 rounded-md bg-white">
      <div className="px-5 py-3 border-b border-slate-200 text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">{title}</div>
      <div className="divide-y divide-slate-100">{children}</div>
    </div>
  );
}
function Row({ k, v }) {
  return (
    <div className="px-5 py-2.5 grid grid-cols-3 gap-2 text-sm">
      <div className="text-slate-500">{k}</div>
      <div className="col-span-2 text-slate-800">{v}</div>
    </div>
  );
}
